/**
 * Map POIs (Points of Interest) Management
 * 
 * This module handles the creation, management, and styling of POIs on Mapbox maps.
 * It includes SVG recoloring functionality for accessibility icons to match theme colors.
 * 
 * Features:
 * - Automatic SVG recoloring based on theme colors
 * - Support for custom icon colors per POI
 * - Clustering for better performance
 * - Accessibility icon support
 * 
 * Usage:
 * 
 * // Basic POI with theme colors
 * const pois = [{
 *   title: "Accessible Entrance",
 *   description: "Main accessible entrance",
 *   location: [longitude, latitude],
 *   icon: "accessibility-1"
 * }];
 * 
 * // POI with custom icon color
 * const pois = [{
 *   title: "Special Access",
 *   description: "Special access point",
 *   location: [longitude, latitude],
 *   icon: "accessibility-2",
 *   bgColor: "#ff0000"  // Custom red color
 * }];
 * 
 * await addPOIs({ map, pois, bgColor, textColor, setTooltip, scenes, slug });
 */
import mapboxgl from 'mapbox-gl';
import PopupMap from "./PopupMap.svelte";
import { recolorSvg, createDataUri } from "./svgRecolor.js";

function getBlackOrWhite(color) {
  return parseInt(color.replace("#", ""), 16) > 0xffffff / 2
    ? "#000000"
    : "#ffffff";
}

function getPopup(poi, scenes) {
  if (!poi) return;
  const { title, description } = poi;
  let { sceneIds, location } = poi;
  // Parse JSON strings
  if (typeof sceneIds === "string") sceneIds = JSON.parse(sceneIds);
  if (typeof location === "string") location = JSON.parse(location);

  // convert the scene ids into linked scenes
  const linkedscenes = sceneIds?.map((sceneId) => {
    const scene = scenes?.find((scene) => scene.id === sceneId);
    return {
      id: sceneId,
      name: scene?.name,
      title: scene?.title,
    };
  });

  return {
    title,
    content: description,
    linkedscenes,
    directions: location,
  };
}

// get a darker or lighter color for hover effect (depending on darkness of color)
function getHoverColor(color) {
  const colorInt = parseInt(color.replace("#", ""), 16);
  const isDark = colorInt > 0xffffff / 2;
  const factor = isDark ? 0.9 : 1.1;
  const newColorInt = Math.round(colorInt * factor);
  return "#" + newColorInt.toString(16);
}

/**
 * Load and recolor an SVG icon for use in Mapbox
 * @param {string} iconPath - Path to the SVG file
 * @param {string} color - Color to apply to the SVG
 * @returns {Promise<HTMLImageElement>} Promise that resolves to an Image element
 */
async function loadRecoloredIcon(iconPath, color) {
  try {
    const response = await fetch(iconPath, {
      mode: 'cors',
      credentials: 'omit'
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const svgText = await response.text();
    
    // Recolor the SVG
    const recoloredSvg = recolorSvg(svgText, color);
    const dataUri = createDataUri(recoloredSvg);
    
    // Create and return an Image element
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUri;
    });
  } catch (error) {
    console.error('Failed to load or recolor icon:', error);
    throw error;
  }
}

export function removePois({ map }) {
  console.log("removePois");
  // Define symbol icons (accessibility + pin)
  const symbolIcons = ['accessibility-1', 'accessibility-2', 'accessibility-3', 'accessibility-4', 'pin'];

  // Remove event listeners if they exist
  if (map._poiListeners) {
    Object.entries(map._poiListeners.clusters).forEach(([event, handler]) => {
      map.off(event, "clusters", handler);
    });
    Object.entries(map._poiListeners.unclusteredPointDefault).forEach(([event, handler]) => {
      map.off(event, "unclustered-point-default", handler);
    });
    // Remove symbol icon event listeners
    symbolIcons.forEach(iconName => {
      const listenerKey = `unclusteredPoint${iconName.charAt(0).toUpperCase() + iconName.slice(1).replace(/-/g, '')}`;
      if (map._poiListeners[listenerKey]) {
        Object.entries(map._poiListeners[listenerKey]).forEach(([event, handler]) => {
          map.off(event, `unclustered-point-${iconName}`, handler);
        });
      }
    });
    delete map._poiListeners;
  }

  // Remove all POI-related layers
  const poiLayers = ['clusters', 'cluster-count', 'unclustered-point-default', ...symbolIcons.map(icon => `unclustered-point-${icon}`)];
  poiLayers.forEach(layer => {
    if (map.getLayer(layer)) {
      map.removeLayer(layer);
    }
  });

  // Remove the POI source
  if (map.getSource('pois')) {
    map.removeSource('pois');
  }
}

export async function addPOIs({ map, pois, bgColor, textColor, setTooltip, scenes, slug }) {
  // filter out hidden pois and sort by Map Menu order, then by name
  pois = pois.filter((poi) => !poi.hidden).sort((a, b) => {
    const oA = a.order ?? 0, oB = b.order ?? 0;
    if (oA !== oB) return oA - oB;
    return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
  });
  // setup colors
  bgColor = bgColor || "#000000";
  textColor = textColor || getBlackOrWhite(bgColor);
  const strokeColor = textColor;
  // just use the same color as the points for now
  const clusterColor = bgColor;
  // determine if cluster text color should be white or black based on darkness of cluster color
  const clusterTextColor = textColor;

  // Symbol icons (accessibility + pin) that use SVG layers
  const symbolIcons = ['accessibility-1', 'accessibility-2', 'accessibility-3', 'accessibility-4', 'pin'];

  // Get unique symbol icons used by POIs
  const usedIcons = [...new Set(pois
    .filter(poi => poi.icon && symbolIcons.includes(poi.icon))
    .map(poi => poi.icon)
  )];

  console.log(`Loading ${usedIcons.length} symbol icons:`, usedIcons);

  // Load and recolor only the symbol icons that are actually used
  for (const iconName of usedIcons) {
    if (!map.hasImage(`${iconName}-icon`)) {
      try {
        const iconPath = `./images/accessibility/${iconName}.svg`;
        const img = await loadRecoloredIcon(iconPath, bgColor);
        img.width = 30;
        img.height = 30;
        map.addImage(`${iconName}-icon`, img);
        console.log(`Loaded and recolored icon: ${iconName} with color ${bgColor}`);
      } catch (error) {
        console.error(`Failed to load icon ${iconName}:`, error);
        // Fallback to original loading method
        const img = new Image();
        img.width = 30;
        img.height = 30;
        img.onload = () => {
          map.addImage(`${iconName}-icon`, img);
        };
        img.src = `./images/accessibility/${iconName}.svg`;
      }
    }
  }

  // Load custom colored icons for POIs that specify custom colors
  const customColorPOIs = pois.filter(poi => poi.icon && symbolIcons.includes(poi.icon) && poi.bgColor);
  for (const poi of customColorPOIs) {
    const iconName = poi.icon;
    const customIconId = `${iconName}-${poi.bgColor.replace('#', '')}-icon`;
    
    if (!map.hasImage(customIconId)) {
      try {
        const iconPath = `./images/accessibility/${iconName}.svg`;
        const img = await loadRecoloredIcon(iconPath, poi.bgColor);
        img.width = 30;
        img.height = 30;
        map.addImage(customIconId, img);
        console.log(`Loaded custom colored icon: ${iconName} with color ${poi.bgColor}`);
      } catch (error) {
        console.error(`Failed to load custom icon ${iconName}:`, error);
      }
    }
  }

  const geojsonSource = {
    type: "FeatureCollection",
    features: pois.map((poi) => ({
      type: "Feature",
      properties: {
        ...poi,
        icon: poi.icon || "default", // Add icon for filtering
      },
      geometry: {
        type: "Point",
        coordinates: poi.location,
      },
    })),
  };

  map.addSource("pois", {
    type: "geojson",
    data: geojsonSource,
    cluster: true,
    clusterRadius: 50,
    clusterMaxZoom: 14,
  });

  map.addLayer({
    id: "clusters",
    type: "circle",
    source: "pois",
    filter: ["has", "point_count"],
    paint: {
      "circle-color": clusterColor,
      "circle-radius": 24,
      "circle-stroke-width": 2,
      "circle-stroke-color": clusterTextColor,
    },
  });

  map.addLayer({
    id: "cluster-count",
    type: "symbol",
    source: "pois",
    filter: ["has", "point_count"],
    layout: {
      "text-field": "{point_count_abbreviated}",
      "text-font": ["Arial Unicode MS Bold", "DIN Offc Pro Bold"],
      "text-size": 12,
    },
    paint: {
      "text-color": clusterTextColor,
    },
  });

  // Add default circle POIs
  map.addLayer({
    id: "unclustered-point-default",
    type: "circle",
    source: "pois",
    filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "icon"], "default"]],
    paint: {
      "circle-color": bgColor,
      "circle-radius": 10,
      "circle-stroke-width": 2,
      "circle-stroke-color": strokeColor,
    },
  });

  // Add accessibility icon POIs (only for icons that are actually used)
  usedIcons.forEach(iconName => {
    map.addLayer({
      id: `unclustered-point-${iconName}`,
      type: "symbol",
      source: "pois",
      filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "icon"], iconName]],
      layout: {
        "icon-image": [
          "case",
          ["has", "bgColor"],
          ["concat", ["get", "icon"], "-", ["slice", ["get", "bgColor"], 1], "-icon"],
          `${iconName}-icon`
        ],
        "icon-allow-overlap": true,
      },
    });
  });

  // Store event listeners for cleanup
  const listeners = {
    clusters: {
      click: (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ["clusters"],
        });
        const clusterId = features[0].properties.cluster_id;
        const clusterSource = map.getSource("pois");

        clusterSource.getClusterLeaves(
          clusterId,
          Infinity,
          0,
          (error, points) => {
            if (error) return;
            const coordinates = points.map((point) => point.geometry.coordinates);
            const bounds = coordinates.reduce(
              (bounds, coord) => bounds.extend(coord),
              new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
            );
            map.fitBounds(bounds, {
              padding: 100,
              maxZoom: 17,
            });
          },
        );
      },
      mouseenter: () => {
        map.getCanvas().style.cursor = "pointer";
      },
      mousemove: (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ["clusters"],
        });
        if (features.length) {
          map.getCanvas().style.cursor = "pointer";
        } else {
          map.getCanvas().style.cursor = "";
        }
      },
      mouseleave: () => {
        map.getCanvas().style.cursor = "";
      }
    },
    unclusteredPointDefault: {
      click: (e) => {
        console.log("poi click", e.features[0].properties);
        const popup = getPopup(e.features?.[0]?.properties, scenes);
        const popupMap = new PopupMap({
          target: document.body,
          props: popup,
        });
      },
      mousemove: (e) => {
        const x = e.point.x;
        const y = e.point.y;
        const text = e.features[0]?.properties?.title;
        if (text) setTooltip({ x, y, text });

        const popup = getPopup(e.features?.[0]?.properties, scenes);
        if (popup) {
          map.getCanvas().style.cursor = "pointer";
        }
      },
      mouseleave: () => {
        map.getCanvas().style.cursor = "";
        setTooltip(null);
      }
    },
    // Accessibility icon event listeners will be added dynamically
  };

  // Create event listeners for each accessibility icon that is actually used
  usedIcons.forEach(iconName => {
    listeners[`unclusteredPoint${iconName.charAt(0).toUpperCase() + iconName.slice(1).replace('-', '')}`] = {
      click: (e) => {
        console.log("poi click", e.features[0].properties);
        const popup = getPopup(e.features?.[0]?.properties, scenes);
        const popupMap = new PopupMap({
          target: document.body,
          props: popup,
        });
      },
      mousemove: (e) => {
        const x = e.point.x;
        const y = e.point.y;
        const text = e.features[0]?.properties?.title;
        if (text) setTooltip({ x, y, text });

        const popup = getPopup(e.features?.[0]?.properties, scenes);
        if (popup) {
          map.getCanvas().style.cursor = "pointer";
        }
      },
      mouseleave: () => {
        map.getCanvas().style.cursor = "";
        setTooltip(null);
      }
    };
  });

  // Add event listeners
  Object.entries(listeners.clusters).forEach(([event, handler]) => {
    map.on(event, "clusters", handler);
  });

  Object.entries(listeners.unclusteredPointDefault).forEach(([event, handler]) => {
    map.on(event, "unclustered-point-default", handler);
  });

  // Add event listeners for symbol icons that are actually used
  usedIcons.forEach(iconName => {
    const listenerKey = `unclusteredPoint${iconName.charAt(0).toUpperCase() + iconName.slice(1).replace(/-/g, '')}`;
    Object.entries(listeners[listenerKey]).forEach(([event, handler]) => {
      map.on(event, `unclustered-point-${iconName}`, handler);
    });
  });

  // Store listeners on the map object for cleanup
  map._poiListeners = listeners;
}



export function addNumericPOIs({ map, pois, bgColor, textColor, setTooltip, scenes }) {
  // filter out hidden pois
  pois = pois.filter((poi) => !poi.hidden);
  // sort pois by Map Menu order, then by name
  pois = pois.sort((a, b) => {
    const oA = a.order ?? 0, oB = b.order ?? 0;
    if (oA !== oB) return oA - oB;
    return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
  });
  // setup colors
  bgColor = bgColor || "#000000";
  textColor = textColor || getBlackOrWhite(bgColor);
  const strokeColor = textColor;

  const geojsonSource = {
    type: "FeatureCollection",
    features: pois.map((poi, index) => ({
      type: "Feature",
      properties: {
        ...poi,
        number: index + 1, // Add numeric property
      },
      geometry: {
        type: "Point",
        coordinates: poi.location,
      },
    })),
  };

  map.addSource("numeric-pois", {
    type: "geojson",
    data: geojsonSource,
  });

  // Add circle background layer
  map.addLayer({
    id: "numeric-points",
    type: "circle",
    source: "numeric-pois",
    paint: {
      "circle-color": bgColor,
      "circle-radius": 12,
      "circle-stroke-width": 2,
      "circle-stroke-color": strokeColor,
    },
  });

  // Add number labels
  map.addLayer({
    id: "numeric-labels",
    type: "symbol",
    source: "numeric-pois",
    layout: {
      "text-field": "{number}",
      "text-font": ["Arial Unicode MS Bold"],
      "text-size": 12,
      "text-allow-overlap": true,
    },
    paint: {
      "text-color": textColor,
    },
  });

  // Store event listeners for cleanup
  const listeners = {
    numericPoints: {
      click: (e) => {
        console.log("numeric poi click", e.features[0].properties);
        const popup = getPopup(e.features?.[0]?.properties, scenes);
        const popupMap = new PopupMap({
          target: document.body,
          props: popup,
        });
      },
      mousemove: (e) => {
        const x = e.point.x;
        const y = e.point.y;
        const text = e.features[0]?.properties?.title;
        if (text) setTooltip({ x, y, text });

        const popup = getPopup(e.features?.[0]?.properties, scenes);
        if (popup) {
          map.getCanvas().style.cursor = "pointer";
        }
      },
      mouseleave: () => {
        map.getCanvas().style.cursor = "";
        setTooltip(null);
      }
    }
  };

  // Add event listeners
  Object.entries(listeners.numericPoints).forEach(([event, handler]) => {
    map.on(event, "numeric-points", handler);
  });

  // Store listeners on the map object for cleanup
  map._numericPoiListeners = listeners;
}

export function removeNumericPois({ map }) {
  console.log("removeNumericPois");
  // Remove event listeners if they exist
  if (map._numericPoiListeners) {
    Object.entries(map._numericPoiListeners.numericPoints).forEach(([event, handler]) => {
      map.off(event, "numeric-points", handler);
    });
    delete map._numericPoiListeners;
  }

  // Remove all numeric POI layers
  const numericLayers = ['numeric-points', 'numeric-labels'];
  numericLayers.forEach(layer => {
    if (map.getLayer(layer)) {
      map.removeLayer(layer);
    }
  });

  // Remove the numeric POI source
  if (map.getSource('numeric-pois')) {
    map.removeSource('numeric-pois');
  }
}
